import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { FileText, MapPin, Search, Send } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type Tool = "select" | "hand" | "highlight" | "rect" | "circle" | "freehand" | "pin";

const TOOL_COLORS = [
  { name: "Yellow", stroke: "#f59e0b", fill: "#facc1530" },
  { name: "Blue", stroke: "#0ea5e9", fill: "#00000000" },
  { name: "Green", stroke: "#22c55e", fill: "#00000000" },
  { name: "Cyan", stroke: "#06b6d4", fill: "#00000000" },
  { name: "Red", stroke: "#ef4444", fill: "#ef4444" },
  { name: "Purple", stroke: "#8b5cf6", fill: "#00000000" },
  { name: "Pink", stroke: "#ec4899", fill: "#00000000" },
  { name: "Orange", stroke: "#f97316", fill: "#00000000" },
  { name: "Black", stroke: "#111827", fill: "#00000000" },
];

export const DEFAULT_TOOL_COLORS: Record<Exclude<Tool, "select" | "hand">, string> = {
  highlight: "#f59e0b",
  rect: "#0ea5e9",
  circle: "#22c55e",
  freehand: "#06b6d4",
  pin: "#ef4444",
};

export function styleForTool(kind: Exclude<Tool, "select" | "hand">, customColor?: string) {
  const color = customColor || DEFAULT_TOOL_COLORS[kind];

  if (kind === "highlight") {
    return { stroke: color, fill: `${color}30`, strokeWidth: 0.002 };
  }
  if (kind === "pin") {
    return { stroke: color, fill: color, strokeWidth: 0.002 };
  }
  return { stroke: color, fill: "#00000000", strokeWidth: 0.002 };
}

export function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function formatPresenceName(entry: { name?: string; userId: string }) {
  return entry.name?.trim() || entry.userId;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getSelectedMentionIds(
  text: string,
  mentionableUsers: Array<{ _id: Id<"users">; name?: string }>
) {
  const activeMentionIds = new Set<string>();

  for (const user of mentionableUsers) {
    const displayName = user.name?.trim();
    if (!displayName) {
      continue;
    }

    const mentionPattern = new RegExp(
      `(^|\\s)@${escapeRegExp(displayName)}(?=\\s|$)`,
      "i"
    );
    if (mentionPattern.test(text)) {
      activeMentionIds.add(String(user._id));
    }
  }

  return activeMentionIds;
}

interface ToolColorPickerProps {
  color: string;
  onChange: (color: string) => void;
}

export function ToolColorPicker({ color, onChange }: ToolColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 rounded-full p-0.5"
        onClick={() => setIsOpen(!isOpen)}
        title="Change color"
      >
        <div
          className="h-5 w-5 rounded-full border-2 border-border"
          style={{ backgroundColor: color }}
        />
      </Button>

      {isOpen ? (
        <div className="absolute left-1/2 top-[calc(100%+10px)] z-[100] w-[160px] -translate-x-1/2 rounded-xl border border-border bg-background p-2.5 shadow-xl">
          <div className="grid grid-cols-3 gap-2">
            {TOOL_COLORS.map((colorOption) => (
              <Button
                key={colorOption.stroke}
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => {
                  onChange(colorOption.stroke);
                  setIsOpen(false);
                }}
                className={cn(
                  "group relative h-8 w-8 rounded-full p-0 transition-all",
                  color === colorOption.stroke
                    ? "ring-2 ring-primary ring-offset-2"
                    : "hover:ring-1 hover:ring-border hover:ring-offset-1"
                )}
                title={colorOption.name}
                aria-label={`Select ${colorOption.name} color`}
              >
                <span
                  className="h-6 w-6 rounded-full border-2 border-border/30 shadow-sm transition-transform group-hover:scale-105"
                  style={{ backgroundColor: colorOption.stroke }}
                />
              </Button>
            ))}
          </div>
          <div className="absolute -top-1.5 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rotate-45 border-l border-t border-border bg-background" />
        </div>
      ) : null}
    </div>
  );
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  mentionableUsers: Array<{ _id: Id<"users">; name?: string }>;
  selectedMentions: Set<string>;
  setSelectedMentions: (mentions: Set<string>) => void;
  onSubmit: () => Promise<void>;
  minRows?: number;
}

function MentionTextarea({
  value,
  onChange,
  placeholder = "Add a comment...",
  mentionableUsers,
  selectedMentions,
  setSelectedMentions,
  onSubmit,
  minRows = 2,
}: MentionTextareaProps) {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const filteredUsers = useMemo(() => {
    if (!mentionQuery) {
      return mentionableUsers
        .filter((user) => (user.name ?? "").trim().length > 0)
        .slice(0, 5);
    }
    const query = mentionQuery.toLowerCase();
    return mentionableUsers
      .filter((user) => {
        const name = (user.name ?? "").trim();
        return name.length > 0 && name.toLowerCase().includes(query);
      })
      .slice(0, 5);
  }, [mentionQuery, mentionableUsers]);

  const handleSubmit = useCallback(async () => {
    if (!value.trim()) {
      return;
    }

    try {
      await onSubmit();
    } catch (error) {
      console.error(error);
      toast.error("Failed to send comment");
    }
  }, [onSubmit, value]);

  const insertMention = (user: { _id: Id<"users">; name?: string }) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const displayName = (user.name ?? "").trim();
    if (!displayName) return;

    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    const textAfterCursor = value.slice(cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
    const newValue = `${textBeforeCursor.slice(0, lastAtIndex)}@${displayName} ${textAfterCursor}`;

    onChange(newValue);
    setSelectedMentions(new Set([...selectedMentions, String(user._id)]));
    setShowMentions(false);
    setMentionQuery("");

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = lastAtIndex + displayName.length + 2;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentions && filteredUsers.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setMentionIndex((index) => (index + 1) % filteredUsers.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setMentionIndex((index) => (index - 1 + filteredUsers.length) % filteredUsers.length);
        return;
      }
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        const user = filteredUsers[mentionIndex];
        if (user) {
          insertMention(user);
        }
        return;
      }
      if (event.key === "Escape") {
        setShowMentions(false);
        return;
      }
    }

    if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && value.trim()) {
      event.preventDefault();
      void handleSubmit();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    onChange(newValue);

    setSelectedMentions(getSelectedMentionIds(newValue, mentionableUsers));

    const cursorPos = event.target.selectionStart;
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const match = textBeforeCursor.match(/@([^\s]*)$/);

    if (match) {
      setMentionQuery(match[1] ?? "");
      setShowMentions(true);
      setMentionIndex(0);
      return;
    }

    setShowMentions(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={minRows}
          className="w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 pr-10 text-sm shadow-sm outline-none ring-offset-background placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
        />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="absolute bottom-1.5 right-1.5 h-7 w-7"
          onClick={() => value.trim() && void handleSubmit()}
          disabled={!value.trim()}
          title="Send (Ctrl+Enter)"
        >
          <Send className="size-4" />
        </Button>
      </div>

      {showMentions && filteredUsers.length > 0 ? (
        <div className="absolute bottom-full left-0 right-0 z-50 mb-1 rounded-md border bg-popover p-1 shadow-md">
          {filteredUsers.map((user, index) => (
            <Button
              key={user._id}
              type="button"
              variant="ghost"
              onClick={() => insertMention(user)}
              className={cn(
                "w-full justify-start rounded-sm px-2 py-1.5 text-left text-sm font-normal",
                index === mentionIndex ? "bg-accent" : "hover:bg-accent/50"
              )}
            >
              <span className="font-medium">{user.name ?? user._id.slice(0, 8)}</span>
            </Button>
          ))}
        </div>
      ) : null}

      <p className="mt-1 text-[10px] text-muted-foreground">
        Use @ to mention • Ctrl+Enter to send
      </p>
    </div>
  );
}

interface ThreadItemProps {
  thread: {
    _id: Id<"revCollabThreads">;
    anchorType: "annotation" | "page";
    status: "open" | "resolved";
    commentCount: number;
    firstCommentBody?: string | null;
    annotationId?: Id<"revCollabAnnotations"> | null;
  };
  isSelected: boolean;
  comments: Array<{
    _id: Id<"revCollabComments">;
    body: string;
    createdByUserId: Id<"users">;
    author?: { name?: string } | null;
  }>;
  userId: string;
  mentionableUsers: Array<{ _id: Id<"users">; name?: string }>;
  onSelect: () => void;
  onSubmitComment: () => Promise<void>;
  onDeleteComment: (args: { commentId: Id<"revCollabComments"> }) => Promise<null | void>;
  onSetStatus: (status: "open" | "resolved") => Promise<null | void>;
  onZoomToAnnotation?: () => void;
  commentBody: string;
  setCommentBody: (body: string) => void;
  selectedMentions: Set<string>;
  setSelectedMentions: (mentions: Set<string>) => void;
}

function getThreadTitle(thread: ThreadItemProps["thread"], comments: ThreadItemProps["comments"]) {
  if (comments.length > 0 && comments[0]?.body) {
    const words = comments[0].body.split(/\s+/);
    return `${words.slice(0, 8).join(" ")}${words.length > 8 ? "..." : ""}`;
  }
  if (thread.firstCommentBody) {
    const words = thread.firstCommentBody.split(/\s+/);
    return `${words.slice(0, 8).join(" ")}${words.length > 8 ? "..." : ""}`;
  }
  return thread.anchorType === "annotation" ? "Annotation thread" : "Page thread";
}

export function ThreadItem({
  thread,
  isSelected,
  comments,
  userId,
  mentionableUsers,
  onSelect,
  onSubmitComment,
  onDeleteComment,
  onSetStatus,
  onZoomToAnnotation,
  commentBody,
  setCommentBody,
  selectedMentions,
  setSelectedMentions,
}: ThreadItemProps) {
  const title = getThreadTitle(thread, comments);

  return (
    <div
      className={cn(
        "rounded-md border transition-colors",
        isSelected ? "border-primary bg-primary/5" : "hover:border-primary/40"
      )}
    >
      <button type="button" onClick={onSelect} className="w-full px-3 py-2 text-left">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {thread.anchorType === "annotation" ? (
              <MapPin className="size-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <FileText className="size-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate text-sm font-medium">{title}</span>
          </div>
          <Badge
            variant={thread.status === "resolved" ? "outline" : "secondary"}
            className="shrink-0 text-xs"
          >
            {thread.status}
          </Badge>
        </div>
        <p className="mt-1 pl-5 text-xs text-muted-foreground">{thread.commentCount} comments</p>
      </button>

      {isSelected ? (
        <div className="border-t px-3 py-2">
          <div className="mb-3 max-h-[200px] space-y-2 overflow-y-auto">
            {comments.length === 0 ? (
              <p className="text-xs text-muted-foreground">No comments yet.</p>
            ) : (
              comments.map((comment) => (
                <div key={comment._id} className="rounded-md border bg-background/50 p-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium">{comment.author?.name ?? "User"}</p>
                    {String(comment.createdByUserId) === userId ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-5 px-1.5 text-[10px]"
                        onClick={async () => {
                          try {
                            await onDeleteComment({ commentId: comment._id });
                          } catch (error) {
                            console.error(error);
                            toast.error("Failed to delete comment");
                          }
                        }}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{comment.body}</p>
                </div>
              ))
            )}
          </div>

          <div className="mb-3 flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={thread.status === "open" ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              onClick={async () => {
                try {
                  await onSetStatus("open");
                } catch (error) {
                  console.error(error);
                  toast.error("Failed to reopen thread");
                }
              }}
              disabled={thread.status === "open"}
            >
              Reopen
            </Button>
            <Button
              type="button"
              size="sm"
              variant={thread.status === "resolved" ? "default" : "outline"}
              className="h-7 px-2 text-xs"
              onClick={async () => {
                try {
                  await onSetStatus("resolved");
                } catch (error) {
                  console.error(error);
                  toast.error("Failed to resolve thread");
                }
              }}
              disabled={thread.status === "resolved"}
            >
              Resolve
            </Button>
            {thread.anchorType === "annotation" && onZoomToAnnotation ? (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="ml-auto h-7 w-7"
                onClick={(event) => {
                  event.stopPropagation();
                  onZoomToAnnotation();
                }}
                title="Zoom to annotation"
              >
                <Search className="size-3.5" />
              </Button>
            ) : null}
          </div>

          <MentionTextarea
            value={commentBody}
            onChange={setCommentBody}
            placeholder="Reply to thread..."
            mentionableUsers={mentionableUsers}
            selectedMentions={selectedMentions}
            setSelectedMentions={setSelectedMentions}
            onSubmit={onSubmitComment}
          />
        </div>
      ) : null}
    </div>
  );
}

interface PageCommentInputProps {
  mentionableUsers: Array<{ _id: Id<"users">; name?: string }>;
  onSubmit: (body: string, mentionIds: string[]) => Promise<void>;
}

export function PageCommentInput({ mentionableUsers, onSubmit }: PageCommentInputProps) {
  const [body, setBody] = useState("");
  const [selectedMentions, setSelectedMentions] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!body.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit(body, Array.from(selectedMentions));
      setBody("");
      setSelectedMentions(new Set());
    } catch (error) {
      console.error(error);
      toast.error("Failed to post page comment");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MentionTextarea
      value={body}
      onChange={setBody}
      placeholder="Start a new discussion..."
      mentionableUsers={mentionableUsers}
      selectedMentions={selectedMentions}
      setSelectedMentions={setSelectedMentions}
      onSubmit={handleSubmit}
      minRows={2}
    />
  );
}
